import { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ChargeView, TenantAddonView } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { completeClientAction } from '@/features/billing/completeClientAction';
import { MoneyCell } from '@/components/cells';
import { StatusChip } from '@/components/status/StatusChip';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useLocale } from '@/i18n/useLocale';
import { useCanWrite } from '@/auth/authHooks';
import { useTenant } from '@/tenant/useTenant';
import { chargeHowLabel, chargeWhenLabel } from './addonLabels';

export interface ChargeHistoryProps {
  addons: TenantAddonView[];
  addonId?: number;
  busy?: boolean;
  /** Opens the retry confirmation for this charge (the caller runs the retry). */
  onRetry: (addon: TenantAddonView, charge: ChargeView) => void;
  /** The charge whose confirmed retry is running; its Retry is disabled. */
  retryingChargeId?: number | null;
}

interface ChargeRow {
  addon: TenantAddonView;
  charge: ChargeView;
}

function collectRows(addons: TenantAddonView[], addonId: number | undefined): ChargeRow[] {
  const rows: ChargeRow[] = [];
  for (const addon of addons) {
    if (addonId !== undefined && addon.id !== addonId) continue;
    for (const charge of addon.charges) {
      rows.push({ addon, charge });
    }
  }
  rows.sort((left, right) => {
    if (left.charge.periodEnd !== right.charge.periodEnd) {
      return right.charge.periodEnd.localeCompare(left.charge.periodEnd);
    }
    return right.charge.id - left.charge.id;
  });
  return rows;
}

function hasClientSecret(charge: ChargeView): boolean {
  const secret = charge.clientAction?.clientSecret;
  return typeof secret === 'string' && secret.length > 0;
}

export function ChargeHistory({
  addons,
  addonId,
  busy = false,
  onRetry,
  retryingChargeId = null,
}: ChargeHistoryProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tenant = useTenant();
  const canWrite = useCanWrite();
  const describe = useActionError();
  const rows = collectRows(addons, addonId);
  const [completingId, setCompletingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [unsupportedIds, setUnsupportedIds] = useState<number[]>([]);

  async function handleComplete(addon: TenantAddonView, charge: ChargeView) {
    setActionError(null);
    setCompletingId(charge.id);
    try {
      const result = await completeClientAction(tenant.slug, charge.clientAction);
      if (result.kind === 'completed') {
        onRetry(addon, charge);
        return;
      }
      if (result.kind === 'failed') {
        setActionError({
          message: t('billing.actionFailed', { message: result.message ?? '' }),
          fieldKeys: [],
          code: undefined,
          requestId: undefined,
        });
        return;
      }
      setUnsupportedIds((ids) => (ids.includes(charge.id) ? ids : [...ids, charge.id]));
    } catch (err) {
      setActionError(describe(err));
    } finally {
      setCompletingId(null);
    }
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <ActionErrorAlert error={actionError} sx={{ m: 2 }} />
      <Table size="small" aria-label={t('addons.chargeHistoryLabel')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('addons.col.when')}</TableCell>
            <TableCell>{t('addons.col.addon')}</TableCell>
            <TableCell align="right">{t('addons.col.amount')}</TableCell>
            <TableCell>{t('addons.col.how')}</TableCell>
            <TableCell>{t('addons.col.chargeStatus')}</TableCell>
            <TableCell align="right">
              <Box component="span" sx={visuallyHidden}>
                {t('common.actions')}
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ addon, charge }) => {
            const retryable = charge.status === 'FAILED' || charge.status === 'ACTION_REQUIRED';
            const showComplete =
              canWrite &&
              charge.status === 'ACTION_REQUIRED' &&
              hasClientSecret(charge) &&
              !unsupportedIds.includes(charge.id);
            return (
              <TableRow key={`${addon.id}-${charge.id}`}>
                <TableCell component="th" scope="row">
                  {chargeWhenLabel(charge, locale)}
                </TableCell>
                <TableCell>{addon.name}</TableCell>
                <MoneyCell minor={charge.amountMinor} currency={charge.currency} />
                <TableCell>{chargeHowLabel(charge)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    <StatusChip domain="addonCharge" status={charge.status} />
                    {charge.status === 'ACTION_REQUIRED' ? (
                      <Typography variant="caption">{t('addons.charge.actionRequired')}</Typography>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {showComplete ? (
                    <Button
                      disabled={busy || completingId === charge.id}
                      onClick={() => void handleComplete(addon, charge)}
                    >
                      {t('billing.completeVerification')}
                    </Button>
                  ) : null}
                  {canWrite && retryable ? (
                    <Button
                      disabled={
                        busy || completingId === charge.id || retryingChargeId === charge.id
                      }
                      onClick={() => onRetry(addon, charge)}
                    >
                      {t('addons.retry')}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
        {t('addons.nettedNote')}
      </Typography>
    </Paper>
  );
}
